import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useAtom } from 'jotai';
import { useCallback, useContext, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { StudyLocalizationStatus } from '@axon/rosetta-sdk';
import { FirebaseAppContext } from 'libs.firebase_react';
import { useLocalization, useLocalizationExport } from 'libs.localization.react';
import { Locale, useLocalizeMessage, Text } from 'libs.nucleus';
import { DeviceSelector, selectedDeviceAtom } from 'libs.preview';
import { PageHeader } from 'libs.react';
import { StudyLocaleStatusProgress } from '../../components';
import { AIGeneratedBanner } from '../../components/ai_generated_banner/AI_generated_banner.component';
import { AssetsInfoPanel } from '../../components/assets_info_panel';
import { AssetReviewScreenList } from '../../components/review_screenlist/asset_review_screenlist.component';
import { TranslationWorkbenchContext } from '../../contexts';
import { deviceList as defaultDeviceList } from '../../utils/device_list';
import { getDevicelistForAssetType } from '../../utils/device_utils';
import { toSentenceCase } from '../../utils/string_utils';
export const ReviewView = ({ assetId, studyId }) => {
    const t = useLocalizeMessage();
    const [selectedDevice] = useAtom(selectedDeviceAtom);
    const { locale = Locale.en_US } = useParams();
    const [queryParams] = useSearchParams();
    const assetType = queryParams.get('assetType');
    const { logEvent } = useContext(FirebaseAppContext);
    const { exportLocalization, isFetching } = useLocalizationExport();
    const { studyLocalizationStatus, resourcesAssets, studyAssets, notificationsAssets } = useContext(TranslationWorkbenchContext);
    const isStudyinDraft = studyLocalizationStatus !== StudyLocalizationStatus.READY;
    const asset = {
        id: assetId,
        type: assetType,
        version: '1',
    };
    const { localizationEntry, localization } = useLocalization(asset);
    const { version: localeVersion, status: localeStatus, localization_origin: localizationOrigin, } = localizationEntry?.supported_locales?.[locale] || {};
    const allAssets = { ...notificationsAssets, ...resourcesAssets, ...studyAssets };
    const twId = localization?.getId();
    const currentAsset = twId ? allAssets[twId] : undefined;
    const pageTitle = currentAsset?.displayName || toSentenceCase(assetType);
    const breadcrumbPages = useMemo(() => [
        { name: t('Translations'), path: `/studies/${studyId}/translations` },
        {
            name: pageTitle,
            path: `/studies/${studyId}/translations/${assetId}?assetType=${assetType}`,
        },
        { name: locale || '', path: `/studies/${studyId}/translations/${assetId}/review/${locale}` },
    ], [studyId, assetId, pageTitle]);
    const handleExport = useCallback(() => {
        if (asset && localization) {
            const viewport = Object.entries(defaultDeviceList).find(([, value]) => value.device === selectedDevice.device)?.[0];
            exportLocalization({
                resources: ['screens'],
                locales: [locale],
                ...(viewport && { viewport }),
                assets: { [localization.getId()]: [`${locale}~${localeVersion}`] },
            });
            logEvent('translations_screen_report_downloaded', {
                context: 'study',
                type: assetType,
                id: assetId,
                locale,
            });
        }
    }, [asset, locale, selectedDevice, localeVersion, localization]);
    const headerOptions = [
        {
            name: t('Export'),
            onClick: handleExport,
            variant: 'primary',
            disabled: isStudyinDraft || isFetching,
            isLoading: isFetching,
        },
    ];
    // @TODO Consume this info from firebase
    const info = [{ title: t('Name'), description: pageTitle }];
    const hasResource = localizationEntry.localization_resources && !!Object.keys(localizationEntry.localization_resources).length;
    const previewOptions = localizationEntry.metadata?.preview;
    const deviceList = getDevicelistForAssetType(assetType);
    const defaultDevice = previewOptions?.default_viewport && deviceList[previewOptions?.default_viewport]
        ? deviceList[previewOptions?.default_viewport]
        : deviceList[Object.keys(deviceList)[0]];
    return (_jsxs(_Fragment, { children: [_jsx(PageHeader, { title: locale, breadcrumbPages: breadcrumbPages, options: headerOptions, customOptions: locale !== Locale.en_US && asset ? (_jsx(StudyLocaleStatusProgress, { asset: asset, locale: locale, assetId: assetId, isDisabled: isStudyinDraft, trackingContext: { context: 'study', type: assetType } })) : (_jsx("span", {})) }), _jsx(AIGeneratedBanner, { status: localeStatus, origin: localizationOrigin }), _jsxs("div", { className: 'flex flex-col gap-6', children: [_jsx(AssetsInfoPanel, { listItems: info, locale: locale, title: t('Information'), version: localeVersion, localizationVersion: {
                            status: localeStatus,
                            version: Number(localeVersion),
                            origin: localizationOrigin,
                        } }), _jsx("hr", {}), _jsxs("div", { className: 'flex flex-col gap-2', children: [_jsx(Text, { size: 'sm', children: t('Device size') }), defaultDevice && (_jsx(DeviceSelector, { devices: deviceList, defaultDevice: defaultDevice, showDimensions: true, width: 'lg' }))] }), hasResource && _jsx(AssetReviewScreenList, { localizationEntry: localizationEntry })] })] }));
};
